import _ from "lodash";
import { picSorter } from "../../../../polaris/util/sort";
import {
  CategoryDto,
  PICGroup,
  DataMapDto,
  CollectionDto,
} from "../../server/dto/datamap/DataMapDto";
import { OrganizationDataRecipientDto } from "../../server/dto/OrganizationDataRecipientDto";
import { BusinessPurposeDto } from "../../server/dto/PersonalInformationOptionsDto";
import { CollectionGroupDetailsDto } from "../../server/dto/CollectionGroupDto";

type PicRecommendations = {
  recommendedPic: CategoryDto[];
  recommendedGroups: PICGroup[];
};

export const groupsFromPIC = (pic: CategoryDto[]): PICGroup[] => {
  const grouped = _.groupBy(pic, "picGroupId");

  return _.sortBy(
    _.map(grouped, (c: CategoryDto[]) => ({
      id: _.get(c, "0.picGroupId"),
      name: _.get(c, "0.picGroupName"),
      order: _.get(c, "0.picGroupOrder"),
      employmentOrder: _.get(c, "0.picGroupEmployeeOrder"),
      key: _.get(c, "0.picGroupKey"),
      categories: c.sort(picSorter),
    })),
    ["picGroupOrder", "picGroupName"],
  );
};

export const recommendationsForDataRecipient = (
  dataMap: DataMapDto,
  dataRecipient: OrganizationDataRecipientDto,
): PicRecommendations => {
  const recommendedPic = Object.values(dataMap.categories).filter((c) =>
    dataRecipient.recommendedPersonalInformationCategories.includes(c.id),
  );

  const recommendedGroups = groupsFromPIC(recommendedPic);

  return { recommendedPic, recommendedGroups };
};

/**
 * Determines the intersection of a vendor's recommended PICs
 * and the collected PICs for the provided collection groups.
 */
export const disclosedRecommendationsForDataRecipient = (
  dataMap: DataMapDto,
  dataRecipient: OrganizationDataRecipientDto,
  collectionGroups: CollectionGroupDetailsDto[],
): PicRecommendations => {
  const groups = new Set(collectionGroups.map((cg) => cg.id));
  const recommendedPic = Object.values(dataMap.categories).filter(
    (c) =>
      dataRecipient.recommendedPersonalInformationCategories.includes(c.id) &&
      dataMap.collection.some(
        (collection) => groups.has(collection.groupId) && collection.picId == c.id,
      ),
  );

  const recommendedGroups = groupsFromPIC(recommendedPic);

  return { recommendedPic, recommendedGroups };
};

export const nonEmploymentBusinessPurposes = (purposes: BusinessPurposeDto[]) =>
  purposes.filter((bp) => bp.name !== "Employment-Related");

export const anyCollectedForCategory = (
  collected: CollectionDto[],
  categories: CategoryDto[],
): boolean => {
  const categoryIds = categories.map((c) => c.id);
  return collected.some((c) => categoryIds.includes(c.picId));
};

export const emptyDataMap = (): DataMapDto => ({
  categories: {},
  picGroups: {},
  collectionGroups: {},
  recipients: {},
  sources: {},
  collection: [],
  disclosure: [],
  received: [],
  recipientAssociations: [],
  sourceAssociations: [],
});

export const isEmptyDataMap = (dataMap: DataMapDto): boolean => {
  if (!dataMap) return true;
  return (
    Object.keys(dataMap.categories).length == 0 &&
    Object.keys(dataMap.picGroups).length == 0 &&
    Object.keys(dataMap.collectionGroups).length == 0 &&
    Object.keys(dataMap.recipients).length == 0 &&
    Object.keys(dataMap.sources).length == 0 &&
    dataMap.collection.length == 0 &&
    dataMap.disclosure.length == 0 &&
    dataMap.received.length == 0 &&
    dataMap.recipientAssociations.length == 0 &&
    dataMap.sourceAssociations.length == 0
  );
};
